import React from "react";
import { ArrowDown, ArrowUp, Globe, GripVertical, MoreHorizontal, Trash2 } from "lucide-react";

import { Chip, IconButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

import type { RuleAction } from "../../types";

const menuItem = "text-[13px] gap-2";

export interface CardDrag {
  armed: boolean;
  dragging: boolean;
  onArm: () => void;
  onStart: () => void;
  onOver: () => void;
  onEnd: () => void;
}

/* One policy on the canvas: its number, name, what it does, its conditions as chips, and
   who it takes. A press opens it in the editor. Dragging starts only from the grip; Move up
   and Move down in its menu do the same from a keyboard or a phone. Everyone else (`n`
   null) is last, with a globe for its number, and has neither. */
export function PolicyCard({ n, title, action, off = false, hit = false, count, chips, says, selected, onSelect, order }: {
  /** Its place, from 1; null for Everyone else. */
  n: number | null;
  title: string;
  action: RuleAction;
  off?: boolean;
  /** The person being checked meets this one first. */
  hit?: boolean;
  /** How many land on it. */
  count: number;
  chips: { text: string; empty: boolean }[];
  says: string;
  selected: boolean;
  onSelect: () => void;
  order?: { first: boolean; last: boolean; onMove: (by: -1 | 1) => void; onDelete: () => void; drag: CardDrag };
}) {
  const drag = order?.drag;
  return (
    <div
      draggable={!!drag?.armed}
      onDragStart={(e) => {
        if (!drag) return;
        e.dataTransfer.effectAllowed = "move";
        drag.onStart();
      }}
      onDragOver={(e) => {
        if (!drag) return;
        e.preventDefault();
        drag.onOver();
      }}
      onDragEnd={drag?.onEnd}
      className={`flex items-start gap-1 p-1.5 rounded-[12px] bg-[var(--surface)] shadow-lift ${
        selected ? "ring-[1.5px] ring-[var(--text-primary)]" : hit ? "ring-[1.5px] ring-[var(--info)]" : "ring-1 ring-[var(--hairline)]"
      } ${drag?.dragging ? "opacity-50" : off ? "opacity-[0.55]" : ""}`}
    >
      <button
        type="button"
        aria-pressed={selected}
        aria-label={n === null ? "Everyone else" : `Policy ${n}: ${title}`}
        onClick={onSelect}
        className="press flex-1 min-w-0 flex items-start gap-2.5 px-1.5 py-1 rounded-[8px] text-left hover:bg-[var(--accent-hover)]"
      >
        <span className="w-[22px] h-[22px] shrink-0 rounded-full ring-1 ring-[var(--hairline-strong)] flex items-center justify-center font-mono tabular-nums text-[11px] text-[var(--text-secondary)]">
          {n === null ? <Globe className="w-3 h-3" strokeWidth={1.75} /> : n}
        </span>
        <span className="min-w-0 flex-1 flex flex-col gap-1.5 pt-0.5">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="min-w-0 truncate text-[13px] font-medium tracking-[-0.005em] text-[var(--text-primary)]">{title}</span>
            <Chip tone={action === "block" ? "danger" : "success"} className="shrink-0 whitespace-nowrap">
              {action === "block" ? "Keeps out" : "Lets in"}
            </Chip>
            {off && <Chip className="shrink-0">Off</Chip>}
            {hit && <Chip tone="info" className="whitespace-nowrap">Matches</Chip>}
            <span
              title={`${count} ${count === 1 ? "person lands" : "people land"} on it`}
              className="ml-auto shrink-0 font-mono tabular-nums text-[11px] text-[var(--text-quaternary)]"
            >
              {count}
            </span>
          </span>
          {chips.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {chips.map((chip, i) => (
                <span
                  key={`${chip.text}-${i}`}
                  className={`max-w-full h-5 px-[7px] rounded-[6px] inline-flex items-center bg-[var(--surface-2)] text-[11.5px] ${
                    chip.empty ? "text-[var(--text-quaternary)]" : "text-[var(--text-secondary)]"
                  }`}
                >
                  <span className="truncate">{chip.text}</span>
                </span>
              ))}
            </span>
          )}
          <span className="text-[11.5px] leading-[1.45] text-[var(--text-tertiary)]">{says}</span>
        </span>
      </button>
      {order && (
        <>
          <IconButton type="button" aria-label={`Drag ${title} to reorder`} onPointerDown={order.drag.onArm} className="cursor-grab">
            <GripVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
          </IconButton>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton type="button" aria-label={`${title} options`} className="data-[state=open]:bg-[var(--accent-hover)]">
                <MoreHorizontal className="w-[14px] h-[14px]" strokeWidth={1.75} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[184px]">
              <DropdownMenuItem className={menuItem} disabled={order.first} onSelect={() => order.onMove(-1)}>
                <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.75} /> Move up
              </DropdownMenuItem>
              <DropdownMenuItem className={menuItem} disabled={order.last} onSelect={() => order.onMove(1)}>
                <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.75} /> Move down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={`${menuItem} text-[var(--danger)] focus:text-[var(--danger)]`} onSelect={order.onDelete}>
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}
